import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { DateRange } from 'react-date-range';
import { subDays, format, differenceInCalendarDays } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import NotificationBar from '../../../ui/atoms/NotificationBar';
import { Button } from '../../../ui/atoms';
import { MetricCard } from '../../../ui';
import { formatIntentLabel, formatTopicLabel } from '../../../features/sentiment';

// =====================================================================
// COLOR PALETTE (cohesive blue/emerald/gray)
// =====================================================================
const COLORS = {
  blue:     '#3b82f6',
  blueMuted:'#93c5fd',
  emerald:  '#10b981',
  emeraldMuted: '#6ee7b7',
  gray:     '#d1d5db',
  grayDark: '#9ca3af',
  orange:   '#f59e0b',
  red:      '#ef4444',
  redMuted: '#fca5a5',
};

// =====================================================================
// REUSABLE COMPONENTS
// =====================================================================

/** Horizontal category bar with hover tooltips rendered via portal (escapes overflow containers) */
const CategoryBar = ({ segments, height = 8 }) => {
  const [hoveredIdx, setHoveredIdx] = React.useState(null);
  const [tipPos, setTipPos] = React.useState({ top: 0, left: 0 });
  const segRefs = React.useRef([]);
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return <div className="w-full bg-gray-100 rounded-full" style={{ height }} />;

  const enriched = segments.map((seg) => {
    const pctW = (seg.value / total) * 100;
    return { ...seg, pctW };
  });

  const handleEnter = (i) => {
    const el = segRefs.current[i];
    if (el) {
      const rect = el.getBoundingClientRect();
      setTipPos({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
    }
    setHoveredIdx(i);
  };

  return (
    <div className="relative">
      <div className="flex w-full rounded-full overflow-hidden" style={{ height }}>
        {enriched.map((seg, i) => {
          if (seg.pctW === 0) return null;
          return (
            <div
              key={i}
              ref={el => segRefs.current[i] = el}
              className="transition-all cursor-pointer first:rounded-l-full last:rounded-r-full"
              style={{ width: `${seg.pctW}%`, backgroundColor: seg.color, opacity: hoveredIdx !== null && hoveredIdx !== i ? 0.45 : 1 }}
              onMouseEnter={() => handleEnter(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            />
          );
        })}
      </div>
      {/* Tooltip via portal - escapes overflow:hidden/auto containers */}
      {hoveredIdx !== null && enriched[hoveredIdx] && enriched[hoveredIdx].pctW > 0 && ReactDOM.createPortal(
        <div 
          className="fixed z-[9999] bg-white border border-gray-100 rounded-lg shadow-lg p-3 min-w-[200px] max-w-[280px] pointer-events-none"
          style={{ top: tipPos.top, left: tipPos.left, transform: 'translateX(-50%)' }}
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: enriched[hoveredIdx].color }} />
            <span className="text-sm font-medium text-gray-900">{enriched[hoveredIdx].label}</span>
          </div>
          <div className="text-sm text-gray-700 tabular-nums">
            {enriched[hoveredIdx].value} <span className="text-gray-400">({enriched[hoveredIdx].pctW.toFixed(0)}% of {total})</span>
          </div>
          {enriched[hoveredIdx].description && (
            <p className="text-xs text-gray-500 mt-1.5 pt-1.5 border-t border-gray-50 leading-relaxed">{enriched[hoveredIdx].description}</p>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

/** Bar list item (Tremor BarList style) */
const BarListItem = ({ name, value, maxValue, color = '#3b82f6' }) => (
  <div className="flex items-center gap-3">
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-sm text-gray-700 truncate" title={name}>{name}</span>
        <span className="text-sm font-medium text-gray-900 tabular-nums ml-3">{value}</span>
      </div>
      <div className="w-full bg-gray-50 rounded h-[6px]">
        <div className="h-[6px] rounded transition-all" style={{ width: `${maxValue > 0 ? (value / maxValue) * 100 : 0}%`, backgroundColor: color }} />
      </div>
    </div>
  </div>
);

// =====================================================================
// DATE RANGE PICKER
// =====================================================================
const PRESETS = [
  { key: 'last7days', days: 7 },
  { key: 'last30days', days: 30 },
  { key: 'last3months', days: 90 },
  { key: 'last6months', days: 180 },
  { key: 'last12months', days: 365 },
];

const DateRangePicker = ({ dateRange, onDateRangeChange, t }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  const endDate = new Date();
  const startDate = subDays(endDate, dateRange - 1);

  const [selection, setSelection] = React.useState({
    startDate,
    endDate,
    key: 'selection'
  });

  // Sync selection when dateRange prop changes
  React.useEffect(() => {
    const end = new Date();
    const start = subDays(end, dateRange - 1);
    setSelection({ startDate: start, endDate: end, key: 'selection' });
  }, [dateRange]);

  // Close on outside click
  React.useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const activePreset = PRESETS.find(p => p.days === dateRange);
  const displayLabel = activePreset
    ? t(`widgets.config.statistics.overview.dateRange.${activePreset.key}`)
    : `${format(selection.startDate, 'MMM d, yyyy')} – ${format(selection.endDate, 'MMM d, yyyy')}`;

  const handleSelect = (ranges) => {
    setSelection(ranges.selection);
  };

  const handleApply = () => {
    const days = differenceInCalendarDays(selection.endDate, selection.startDate) + 1;
    onDateRangeChange(Math.max(days, 1));
    setOpen(false);
  };

  const handlePreset = (days) => {
    onDateRangeChange(days);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      {/* Trigger button */}
      <Button
        type="button"
        variant="secondary"
        size="md"
        shape="square"
        onClick={() => setOpen(!open)}
        className="gap-2 shadow-sm"
      >
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span>{displayLabel}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </Button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 z-50 mt-2 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
          <div className="flex">
            {/* Presets */}
            <div className="w-44 border-r border-gray-100 py-2">
              <p className="px-4 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                {t('widgets.config.statistics.overview.dateRange.quickSelect')}
              </p>
              {PRESETS.map(preset => (
                <button
                  key={preset.days}
                  onClick={() => handlePreset(preset.days)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    dateRange === preset.days
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t(`widgets.config.statistics.overview.dateRange.${preset.key}`)}
                </button>
              ))}
            </div>

            {/* Calendar */}
            <div className="p-3">
              <DateRange
                ranges={[selection]}
                onChange={handleSelect}
                months={2}
                direction="horizontal"
                maxDate={new Date()}
                rangeColors={['#6366f1']}
                showDateDisplay={false}
                color="#6366f1"
              />
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-1">
                <p className="text-xs text-gray-400">
                  {format(selection.startDate, 'MMM d, yyyy')} – {format(selection.endDate, 'MMM d, yyyy')}
                  <span className="ml-1.5 text-gray-300">
                    ({differenceInCalendarDays(selection.endDate, selection.startDate) + 1} {t('widgets.config.statistics.overview.days')})
                  </span>
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOpen(false)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleApply}
                    className="px-4 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                  >
                    {t('widgets.config.statistics.overview.dateRange.apply')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// MAIN COMPONENT
// =====================================================================
const WidgetStatistics = ({ 
  statsData, 
  statsLoading, 
  dateRange, 
  onDateRangeChange,
  widgetId 
}) => {
  const { t } = useTranslation();

  const combinedData = statsData.sessionsByDay?.map((sessionDay, index) => ({
    date: sessionDay.date,
    displayDate: sessionDay.displayDate,
    sessions: sessionDay.count,
    messages: statsData.messagesByDay?.[index]?.count || 0
  })) || [];

  const pct = (value, total) => total > 0 ? ((value / total) * 100).toFixed(0) : '0';

  if (statsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
        <p className="text-sm text-gray-500 mt-4">{t('widgets.config.statistics.loading.title')}</p>
      </div>
    );
  }

  if (!widgetId) {
    return (
      <div className="text-center p-8 rounded-xl bg-gray-50">
        <p className="text-gray-500 text-sm">
          {t('widgets.config.statistics.overview.saveConfigFirst')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pl-0">

      {/* ── HEADER ─────────────────────────── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-lg font-sans font-semibold text-gray-900">
          {t('widgets.config.statistics.overview.title')}
        </h2>
        <DateRangePicker dateRange={dateRange} onDateRangeChange={onDateRangeChange} t={t} />
      </div>

      <NotificationBar variant="info" title={t('widgets.config.statistics.overview.noteTitle')}>
        {t('widgets.config.statistics.overview.noteBody')}
      </NotificationBar>

      {/* ── KEY METRICS ────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <MetricCard label={t('widgets.config.statistics.overview.metrics.totalSessions')} value={statsData.totalSessions.toLocaleString()} />
        <MetricCard label={t('widgets.config.statistics.overview.metrics.totalMessages')} value={statsData.totalMessages.toLocaleString()} />
        <MetricCard label={t('widgets.config.statistics.overview.metrics.totalLeads')} value={statsData.totalLeads?.toLocaleString() || 0} />
      </div>

      {/* ── SESSIONS & MESSAGES AREA CHART ── */}
      {combinedData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-4">
            {t('widgets.config.statistics.overview.sessionsMessages.title')}
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={combinedData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.blue} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={COLORS.blue} stopOpacity={0.01} />
                  </linearGradient>
                  <linearGradient id="gradMessages" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.emerald} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={COLORS.emerald} stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#f3f4f6" />
                <XAxis 
                  dataKey="displayDate" 
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false} tickLine={false}
                  interval={dateRange > 90 ? 'preserveStartEnd' : 0}
                  angle={dateRange > 30 ? -45 : 0}
                  height={dateRange > 30 ? 55 : 30}
                  textAnchor={dateRange > 30 ? 'end' : 'middle'}
                />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const sessionsPoint = payload.find((p) => p.dataKey === 'sessions');
                    const messagesPoint = payload.find((p) => p.dataKey === 'messages');
                    return (
                      <div className="bg-white border border-gray-100 rounded-lg shadow-lg p-3 text-xs">
                        <p className="font-semibold text-gray-900 mb-1">
                          {t('widgets.config.statistics.overview.sessionsMessages.tooltip.date')}: {label}
                        </p>
                        <p className="text-blue-600 font-medium">
                          {t('widgets.config.statistics.overview.sessions')}: {sessionsPoint?.value ?? 0}
                        </p>
                        <p className="text-emerald-600 font-medium">
                          {t('widgets.config.statistics.overview.messages')}: {messagesPoint?.value ?? 0}
                        </p>
                      </div>
                    );
                  }}
                />
                <Area type="monotone" dataKey="sessions" name={t('widgets.config.statistics.overview.sessions')} stroke={COLORS.blue} strokeWidth={2} fill="url(#gradSessions)" dot={false} activeDot={{ r: 4, fill: COLORS.blue, stroke: '#fff', strokeWidth: 2 }} />
                <Area type="monotone" dataKey="messages" name={t('widgets.config.statistics.overview.messages')} stroke={COLORS.emerald} strokeWidth={2} fill="url(#gradMessages)" dot={false} activeDot={{ r: 4, fill: COLORS.emerald, stroke: '#fff', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-5 mt-3 pl-1">
            <div className="flex items-center gap-1.5"><div className="w-3 h-[3px] rounded-full" style={{ backgroundColor: COLORS.blue }} /><span className="text-xs text-gray-500">{t('widgets.config.statistics.overview.sessions')}</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-[3px] rounded-full" style={{ backgroundColor: COLORS.emerald }} /><span className="text-xs text-gray-500">{t('widgets.config.statistics.overview.messages')}</span></div>
          </div>
        </div>
      )}

      {/* ── SENTIMENT TREND ──────── */}
      <div className="grid grid-cols-1 gap-5">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          {(() => {
            const sentimentDays = (statsData.sentimentByDay || []).filter(d => d.total > 0);
            if (sentimentDays.length === 0) {
              return (
                <div className="text-center py-16">
                  <p className="text-sm text-gray-400">
                    {t('widgets.config.statistics.overview.sentimentTrend.noData')}
                  </p>
                </div>
              );
            }
            return (
              <>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-900 font-sans">
                    {t('widgets.config.statistics.overview.sentimentTrend.title')}
                  </h3>
                  <span className="text-xs text-gray-400">
                    {t('widgets.config.statistics.overview.sentimentTrend.days', { count: sentimentDays.length })}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  {t('widgets.config.statistics.overview.sentimentTrend.description')}
                </p>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={sentimentDays} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradSentiment" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COLORS.blue} stopOpacity={0.12} />
                          <stop offset="100%" stopColor={COLORS.blue} stopOpacity={0.01} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="#f3f4f6" />
                      <XAxis dataKey="displayDate" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false}
                        interval={sentimentDays.length > 15 ? 'preserveStartEnd' : 0}
                        angle={sentimentDays.length > 8 ? -45 : 0}
                        height={sentimentDays.length > 8 ? 55 : 30}
                        textAnchor={sentimentDays.length > 8 ? 'end' : 'middle'}
                      />
                      <YAxis domain={[-1, 1]} ticks={[-1, -0.5, 0, 0.5, 1]} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={(v) => v.toFixed(1)} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload;
                        if (!d || d.total === 0) return null;
                        return (
                          <div className="bg-white border border-gray-100 rounded-lg shadow-lg p-3 text-xs">
                            <p className="font-semibold text-gray-900 mb-1">
                              {t('widgets.config.statistics.overview.sentimentTrend.tooltip.date')}: {label}
                            </p>
                            <p className={`font-medium ${d.avgSentiment > 0 ? 'text-emerald-600' : d.avgSentiment < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                              {t('widgets.config.statistics.overview.sentimentTrend.score')}: {d.avgSentiment !== null ? d.avgSentiment.toFixed(2) : t('widgets.config.statistics.overview.notAvailable')}
                            </p>
                            <p className="text-gray-400 mt-1">
                              {t('widgets.config.statistics.overview.sessionsCount', { count: d.total })}
                            </p>
                          </div>
                        );
                      }} />
                      <ReferenceLine y={0} stroke="#e5e7eb" strokeDasharray="4 4" />
                      <Area type="monotone" dataKey="avgSentiment" stroke={COLORS.blue} strokeWidth={2} fill="url(#gradSentiment)" connectNulls
                        dot={({ cx, cy, payload }) => {
                          if (!payload || payload.avgSentiment === null) return null;
                          const c = payload.avgSentiment > 0.25 ? COLORS.emerald : payload.avgSentiment < -0.25 ? COLORS.red : COLORS.grayDark;
                          return <circle key={cx} cx={cx} cy={cy} r={4} fill={c} stroke="#fff" strokeWidth={2} />;
                        }}
                        activeDot={{ r: 5, stroke: COLORS.blue, strokeWidth: 2, fill: '#fff' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                  {t('widgets.config.statistics.overview.sentimentTrend.scale')}
                </p>
              </>
            );
          })()}
        </div>

      </div>

      {/* ── OPERATIONAL INSIGHTS ─────────── */}
      {(statsData.effortStats?.total > 0 || statsData.priorityStats?.total > 0 || statsData.riskStats?.total > 0 || statsData.trajectoryStats?.total > 0 || statsData.loopStats?.total > 0 || statsData.confidenceStats?.total > 0) && (
        <>
          <div className="pt-4">
            <h2 className="text-lg font-semibold text-gray-900 font-sans">
              {t('widgets.config.statistics.overview.operationalInsights.title')}
            </h2>
            <p className="text-sm text-gray-400 mt-0.5 font-sans">
              {t('widgets.config.statistics.overview.operationalInsights.description')}
            </p>
          </div>

          {/* Summary cards - Row 1: Risk & Priority */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {statsData.priorityStats?.total > 0 && (
              <MetricCard label={t('widgets.config.statistics.overview.operationalInsights.actionRequired')} value={`${statsData.priorityStats.actionRequiredPercentage}%`}
                sub={t('widgets.config.statistics.overview.operationalInsights.actionRequiredSub', { count: statsData.priorityStats.actionRequired,
                  total: statsData.priorityStats.total
                })}
                accent={statsData.priorityStats.actionRequired > 0 ? 'text-red-600' : undefined}
                tooltip={t('widgets.config.statistics.overview.operationalInsights.tooltips.actionRequired')}
              />
            )}
            {statsData.riskStats?.total > 0 && (
              <MetricCard label={t('widgets.config.statistics.overview.operationalInsights.exitIntent')} value={`${statsData.riskStats.exitIntentPercentage}%`}
                sub={t('widgets.config.statistics.overview.operationalInsights.exitIntentSub', { count: statsData.riskStats.exitIntentCount,
                  total: statsData.riskStats.total
                })}
                accent={statsData.riskStats.exitIntentCount > 0 ? 'text-orange-600' : undefined}
                tooltip={t('widgets.config.statistics.overview.operationalInsights.tooltips.exitIntent')}
              />
            )}
            {statsData.effortStats?.total > 0 && (
              <MetricCard label={t('widgets.config.statistics.overview.operationalInsights.highEffort')} value={`${statsData.effortStats.highEffortPercentage}%`}
                sub={t('widgets.config.statistics.overview.operationalInsights.highEffortSub', { avg: statsData.effortStats.average,
                  total: statsData.effortStats.total
                })}
                accent={parseFloat(statsData.effortStats.highEffortPercentage) > 20 ? 'text-red-600' : parseFloat(statsData.effortStats.highEffortPercentage) > 0 ? 'text-orange-600' : undefined}
                tooltip={t('widgets.config.statistics.overview.operationalInsights.tooltips.highEffort')}
              />
            )}
            {statsData.loopStats?.total > 0 && (
              <MetricCard label={t('widgets.config.statistics.overview.operationalInsights.loopRate')} value={`${statsData.loopStats.loopPercentage}%`}
                sub={t('widgets.config.statistics.overview.operationalInsights.loopRateSub', { count: statsData.loopStats.loopCount,
                  total: statsData.loopStats.total
                })}
                accent={parseFloat(statsData.loopStats.loopPercentage) > 10 ? 'text-red-600' : parseFloat(statsData.loopStats.loopPercentage) > 0 ? 'text-orange-600' : undefined}
                tooltip={t('widgets.config.statistics.overview.operationalInsights.tooltips.loopRate')}
              />
            )}
          </div>

          {/* Summary cards - Row 2: Tone & Quality */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {statsData.trajectoryStats?.total > 0 && (
              <MetricCard label={t('widgets.config.statistics.overview.operationalInsights.moodDeclining')}
                value={`${statsData.trajectoryStats.total > 0 ? ((statsData.trajectoryStats.declining / statsData.trajectoryStats.total) * 100).toFixed(1) : 0}%`}
                sub={t('widgets.config.statistics.overview.operationalInsights.moodDecliningSub', { count: statsData.trajectoryStats.declining,
                  total: statsData.trajectoryStats.total
                })}
                accent={statsData.trajectoryStats.declining > 0 ? 'text-red-600' : undefined}
                tooltip={t('widgets.config.statistics.overview.operationalInsights.tooltips.moodDeclining')}
              />
            )}
            {statsData.trajectoryStats?.hiddenDips > 0 && (
              <MetricCard
                label={t('widgets.config.statistics.overview.operationalInsights.hiddenDips')}
                value={`${statsData.trajectoryStats.total > 0 ? ((statsData.trajectoryStats.hiddenDips / statsData.trajectoryStats.total) * 100).toFixed(1) : 0}%`}
                sub={t('widgets.config.statistics.overview.operationalInsights.hiddenDipsSub', { count: statsData.trajectoryStats.hiddenDips,
                  total: statsData.trajectoryStats.total
                })}
                accent="text-orange-600"
                tooltip={t('widgets.config.statistics.overview.operationalInsights.tooltips.hiddenDips')}
              />
            )}
            {statsData.riskStats?.total > 0 && (
              <MetricCard label={t('widgets.config.statistics.overview.operationalInsights.incomplete')} value={`${statsData.riskStats.closingMissingPercentage}%`}
                sub={t('widgets.config.statistics.overview.operationalInsights.incompleteSub', { count: statsData.riskStats.closingMissingCount,
                  total: statsData.riskStats.total
                })}
                accent={parseFloat(statsData.riskStats.closingMissingPercentage) > 30 ? 'text-red-600' : parseFloat(statsData.riskStats.closingMissingPercentage) > 10 ? 'text-orange-600' : undefined}
                tooltip={t('widgets.config.statistics.overview.operationalInsights.tooltips.incomplete')}
              />
            )}
            {statsData.confidenceStats?.total > 0 && (
              <MetricCard label={t('widgets.config.statistics.overview.operationalInsights.aiConfidence')} value={`${((parseFloat(statsData.confidenceStats.average) || 0) * 100).toFixed(0)}%`}
                sub={t('widgets.config.statistics.overview.operationalInsights.aiConfidenceSub', { value: statsData.confidenceStats.lowPercentage
                })}
                accent={parseFloat(statsData.confidenceStats.average) < 0.6 ? 'text-red-600' : parseFloat(statsData.confidenceStats.average) < 0.75 ? 'text-orange-600' : 'text-emerald-600'}
                tooltip={t('widgets.config.statistics.overview.operationalInsights.tooltips.aiConfidence')}
              />
            )}
            {(statsData.resolutionStats?.sessionsWithStatus || 0) > 0 && (
              <MetricCard
                label={t('widgets.config.statistics.overview.metrics.resolveRate')}
                value={`${statsData.resolutionStats?.resolutionPercentage || 0}%`}
                sub={t('widgets.config.statistics.overview.metrics.resolvedOf', { resolved: statsData.resolutionStats?.resolved || 0,
                  total: statsData.resolutionStats?.sessionsWithStatus || 0
                })}
              />
            )}
          </div>

          {/* Tone Trajectory (Category Bar) + Priority */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Tone Trajectory - Category Bar */}
            {statsData.trajectoryStats?.total > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-900 font-sans">
                    {t('widgets.config.statistics.overview.toneTrajectory.title')}
                  </h3>
                  <span className="text-xs text-gray-400">
                    {t('widgets.config.statistics.overview.ofSessions', { count: statsData.trajectoryStats.total,
                      total: statsData.totalSessions
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-5">
                  {t('widgets.config.statistics.overview.toneTrajectory.description')}
                </p>
                
                <CategoryBar 
                  height={12}
                  segments={[
                    { label: t('widgets.config.statistics.overview.toneTrajectory.improving'), value: statsData.trajectoryStats.improving, color: COLORS.emerald, description: t('widgets.config.statistics.overview.toneTrajectory.improvingDesc') },
                    { label: t('widgets.config.statistics.overview.toneTrajectory.stable'), value: statsData.trajectoryStats.stable, color: COLORS.gray, description: t('widgets.config.statistics.overview.toneTrajectory.stableDesc') },
                    { label: t('widgets.config.statistics.overview.toneTrajectory.declining'), value: statsData.trajectoryStats.declining, color: COLORS.red, description: t('widgets.config.statistics.overview.toneTrajectory.decliningDesc') },
                  ]}
                />
                <div className="flex items-center justify-between mt-3">
                  {[
                    { label: t('widgets.config.statistics.overview.toneTrajectory.improving'), value: statsData.trajectoryStats.improving, color: COLORS.emerald },
                    { label: t('widgets.config.statistics.overview.toneTrajectory.stable'), value: statsData.trajectoryStats.stable, color: COLORS.gray },
                    { label: t('widgets.config.statistics.overview.toneTrajectory.declining'), value: statsData.trajectoryStats.declining, color: COLORS.red },
                  ].map(s => (
                    <div key={s.label} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-gray-500">{s.label}</span>
                      <span className="text-gray-900 font-medium tabular-nums">{s.value}</span>
                      <span className="text-gray-400">({pct(s.value, statsData.trajectoryStats.total)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Priority Distribution - Category Bar */}
            {statsData.priorityStats?.total > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-900 font-sans">
                    {t('widgets.config.statistics.overview.priorityDistribution.title')}
                  </h3>
                  <span className="text-xs text-gray-400">
                    {t('widgets.config.statistics.overview.ofSessions', { count: statsData.priorityStats.total,
                      total: statsData.totalSessions
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-5">
                  {t('widgets.config.statistics.overview.priorityDistribution.description')}
                </p>
                
                <CategoryBar 
                  height={12}
                  segments={[
                    { label: t('widgets.config.statistics.overview.priorityDistribution.segments.P3.label'), value: statsData.priorityStats.distribution.P3 || 0, color: COLORS.emerald, description: t('widgets.config.statistics.overview.priorityDistribution.segments.P3.description') },
                    { label: t('widgets.config.statistics.overview.priorityDistribution.segments.P2.label'), value: statsData.priorityStats.distribution.P2 || 0, color: COLORS.blueMuted, description: t('widgets.config.statistics.overview.priorityDistribution.segments.P2.description') },
                    { label: t('widgets.config.statistics.overview.priorityDistribution.segments.P1.label'), value: statsData.priorityStats.distribution.P1 || 0, color: COLORS.orange, description: t('widgets.config.statistics.overview.priorityDistribution.segments.P1.description') },
                    { label: t('widgets.config.statistics.overview.priorityDistribution.segments.P0.label'), value: statsData.priorityStats.distribution.P0 || 0, color: COLORS.red, description: t('widgets.config.statistics.overview.priorityDistribution.segments.P0.description') },
                  ]}
                />
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
                  {[
                    { label: 'P3', value: statsData.priorityStats.distribution.P3 || 0, color: COLORS.emerald },
                    { label: 'P2', value: statsData.priorityStats.distribution.P2 || 0, color: COLORS.blueMuted },
                    { label: 'P1', value: statsData.priorityStats.distribution.P1 || 0, color: COLORS.orange },
                    { label: 'P0', value: statsData.priorityStats.distribution.P0 || 0, color: COLORS.red },
                  ].map(s => (
                    <div key={s.label} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-gray-500">{s.label}</span>
                      <span className="text-gray-900 font-medium tabular-nums">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Effort + Risk - 50/50 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Effort Distribution - Category Bar */}
            {statsData.effortStats?.total > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-900 font-sans">
                    {t('widgets.config.statistics.overview.effortDistribution.title')}
                  </h3>
                  <span className="text-xs text-gray-400">
                    {t('widgets.config.statistics.overview.ofSessions', { count: statsData.effortStats.total,
                      total: statsData.totalSessions
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-5">
                  {t('widgets.config.statistics.overview.effortDistribution.description')}
                </p>
                
                <CategoryBar 
                  height={12}
                  segments={[
                    { label: t('widgets.config.statistics.overview.effortDistribution.segments.1.label'), value: statsData.effortStats.distribution[1] || 0, color: COLORS.emerald, description: t('widgets.config.statistics.overview.effortDistribution.segments.1.description') },
                    { label: t('widgets.config.statistics.overview.effortDistribution.segments.2.label'), value: statsData.effortStats.distribution[2] || 0, color: COLORS.emerald, description: t('widgets.config.statistics.overview.effortDistribution.segments.2.description') },
                    { label: t('widgets.config.statistics.overview.effortDistribution.segments.3.label'), value: statsData.effortStats.distribution[3] || 0, color: COLORS.blueMuted, description: t('widgets.config.statistics.overview.effortDistribution.segments.3.description') },
                    { label: t('widgets.config.statistics.overview.effortDistribution.segments.4.label'), value: statsData.effortStats.distribution[4] || 0, color: COLORS.orange, description: t('widgets.config.statistics.overview.effortDistribution.segments.4.description') },
                    { label: t('widgets.config.statistics.overview.effortDistribution.segments.5.label'), value: statsData.effortStats.distribution[5] || 0, color: COLORS.red, description: t('widgets.config.statistics.overview.effortDistribution.segments.5.description') },
                  ]}
                />
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
                  {[
                    { label: t('widgets.config.statistics.overview.effortDistribution.easy'), color: COLORS.emerald, value: (statsData.effortStats.distribution[1] || 0) + (statsData.effortStats.distribution[2] || 0) },
                    { label: t('widgets.config.statistics.overview.effortDistribution.moderate'), color: COLORS.blueMuted, value: statsData.effortStats.distribution[3] || 0 },
                    { label: t('widgets.config.statistics.overview.effortDistribution.hard'), color: COLORS.red, value: (statsData.effortStats.distribution[4] || 0) + (statsData.effortStats.distribution[5] || 0) },
                  ].map(s => (
                    <div key={s.label} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-gray-500">{s.label}</span>
                      <span className="text-gray-900 font-medium tabular-nums">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Risk Indicators - Category Bar */}
            {statsData.riskStats?.total > 0 && (() => {
              const exitIntent = statsData.riskStats.exitIntentCount || 0;
              const incomplete = statsData.riskStats.closingMissingCount || 0;
              const highEffort = statsData.effortStats?.highEffortCount || 0;
              const unresolved = statsData.resolutionStats?.unresolved || 0;
              const loops = statsData.loopStats?.loopCount || 0;
              const noRisk = Math.max(0, statsData.riskStats.total - exitIntent - incomplete - highEffort - unresolved - loops);
              return (
                <div className="bg-white rounded-xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-900 font-sans">
                    {t('widgets.config.statistics.overview.riskIndicators.title')}
                  </h3>
                  <span className="text-xs text-gray-400">
                    {t('widgets.config.statistics.overview.ofSessions', { count: statsData.riskStats.total,
                      total: statsData.totalSessions
                    })}
                  </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-5">
                    {t('widgets.config.statistics.overview.riskIndicators.description')}
                  </p>
                  
                  <CategoryBar 
                    height={12}
                    segments={[
                      { label: t('widgets.config.statistics.overview.riskIndicators.noRisk'), value: noRisk, color: COLORS.emerald, description: t('widgets.config.statistics.overview.riskIndicators.noRiskDesc') },
                      { label: t('widgets.config.statistics.overview.riskIndicators.loop'), value: loops, color: '#a78bfa', description: t('widgets.config.statistics.overview.riskIndicators.loopDesc') },
                      { label: t('widgets.config.statistics.overview.riskIndicators.highEffort'), value: highEffort, color: COLORS.blueMuted, description: t('widgets.config.statistics.overview.riskIndicators.highEffortDesc') },
                      { label: t('widgets.config.statistics.overview.riskIndicators.unresolved'), value: unresolved, color: COLORS.redMuted, description: t('widgets.config.statistics.overview.riskIndicators.unresolvedDesc') },
                      { label: t('widgets.config.statistics.overview.riskIndicators.incomplete'), value: incomplete, color: COLORS.orange, description: t('widgets.config.statistics.overview.riskIndicators.incompleteDesc') },
                      { label: t('widgets.config.statistics.overview.riskIndicators.exitIntent'), value: exitIntent, color: COLORS.red, description: t('widgets.config.statistics.overview.riskIndicators.exitIntentDesc') },
                    ]}
                  />
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
                    {[
                      { label: t('widgets.config.statistics.overview.riskIndicators.noRisk'), value: noRisk, color: COLORS.emerald },
                      { label: t('widgets.config.statistics.overview.riskIndicators.loop'), value: loops, color: '#a78bfa' },
                      { label: t('widgets.config.statistics.overview.riskIndicators.highEffort'), value: highEffort, color: COLORS.blueMuted },
                      { label: t('widgets.config.statistics.overview.riskIndicators.unresolved'), value: unresolved, color: COLORS.redMuted },
                      { label: t('widgets.config.statistics.overview.riskIndicators.incomplete'), value: incomplete, color: COLORS.orange },
                      { label: t('widgets.config.statistics.overview.riskIndicators.exitIntent'), value: exitIntent, color: COLORS.red },
                    ].map(s => (
                      <div key={s.label} className="flex items-center gap-1.5 text-xs">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-gray-500">{s.label}</span>
                        <span className="text-gray-900 font-medium tabular-nums">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </>
      )}

      {/* ── QUALITATIVE ANALYSIS ─────────── */}
      {statsData.qualitativeStats && (
        (statsData.qualitativeStats.topTopics?.length > 0) || 
        (statsData.qualitativeStats.topIntents?.length > 0) ||
        (statsData.qualitativeStats.topCategories?.length > 0)
      ) && (
        <>
          <div className="pt-4">
            <h2 className="text-lg font-sans font-semibold text-gray-900">
              {t('widgets.config.statistics.overview.qualitativeAnalysis.title')}
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">
              {t('widgets.config.statistics.overview.qualitativeAnalysis.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Topics */}
            {statsData.qualitativeStats.topTopics?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-900 font-sans">
                    {t('widgets.config.statistics.overview.topTopics.title')}
                  </h3>
                  <span className="text-xs text-gray-400">
                    {t('widgets.config.statistics.overview.ofSessions', { count: statsData.qualitativeStats.totalSessionsWithTopics,
                      total: statsData.totalSessions
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  {t('widgets.config.statistics.overview.topTopics.description')}
                </p>
                <div className="space-y-2.5">
                  {statsData.qualitativeStats.topTopics.map((topic, i) => (
                    <BarListItem
                      key={i}
                      name={formatTopicLabel(topic.name, t)}
                      value={topic.count}
                      maxValue={statsData.qualitativeStats.topTopics[0].count}
                      color={COLORS.blue}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Intents */}
            {(() => {
              const intents = statsData.qualitativeStats.topIntents?.length > 0 
                ? statsData.qualitativeStats.topIntents 
                : statsData.qualitativeStats.topCategories;
              const total = statsData.qualitativeStats.topIntents?.length > 0
                ? statsData.qualitativeStats.totalSessionsWithIntents
                : statsData.qualitativeStats.totalSessionsWithCategories;
              if (!intents || intents.length === 0) return null;

              return (
                <div className="bg-white rounded-xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-medium text-gray-900 font-sans">
                      {t('widgets.config.statistics.overview.customerIntents.title')}
                    </h3>
                    <span className="text-xs text-gray-400">
                      {t('widgets.config.statistics.overview.ofSessions', { count: total,
                        total: statsData.totalSessions
                      })}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-4">
                    {t('widgets.config.statistics.overview.customerIntents.description')}
                  </p>
                  <div className="space-y-2.5">
                    {intents.map((intent, i) => (
                      <BarListItem key={i} name={formatIntentLabel(intent.name, t)} value={intent.count} maxValue={intents[0].count} color={COLORS.emerald} />
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Intent × Resolve Cross-Tab */}
          {statsData.intentResolveStats?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-gray-900 font-sans">
                  {t('widgets.config.statistics.overview.intentResolve.title')}
                </h3>
                <span className="text-xs text-gray-400">
                  {t('widgets.config.statistics.overview.sessionsCount', { count: statsData.intentResolveStats.reduce((a, r) => a + r.total, 0)
                  })}
                </span>
              </div>
              <p className="text-xs text-gray-400 mb-4">
                {t('widgets.config.statistics.overview.intentResolve.description')}
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs font-medium text-gray-500 pb-3 pr-4">{t('widgets.config.statistics.overview.table.intent')}</th>
                      <th className="text-right text-xs font-medium text-gray-500 pb-3 px-3">{t('widgets.config.statistics.overview.table.sessions')}</th>
                      <th className="text-right text-xs font-medium text-gray-500 pb-3 px-3">{t('widgets.config.statistics.overview.table.resolveRate')}</th>
                      <th className="text-right text-xs font-medium text-gray-500 pb-3 px-3">{t('widgets.config.statistics.overview.table.avgEffort')}</th>
                      <th className="text-left text-xs font-medium text-gray-500 pb-3 pl-3 w-[180px] min-w-[140px]">{t('widgets.config.statistics.overview.table.resolutionBreakdown')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsData.intentResolveStats.map((row, i) => {
                      const resolveRate = parseFloat(row.resolveRate);
                      const avgEffort = parseFloat(row.avgEffort);
                      const rateColor = resolveRate >= 80 ? 'text-emerald-600' : resolveRate >= 50 ? 'text-orange-600' : 'text-red-600';
                      const effortColor = isNaN(avgEffort) ? 'text-gray-400' : avgEffort <= 2 ? 'text-emerald-600' : avgEffort <= 3 ? 'text-gray-700' : 'text-red-600';
                      return (
                        <tr key={i} className="border-b border-gray-50 last:border-0">
                          <td className="py-2.5 pr-4 text-gray-900 font-medium">{formatIntentLabel(row.intent, t)}</td>
                          <td className="py-2.5 px-3 text-right text-gray-700 tabular-nums">{row.total}</td>
                          <td className={`py-2.5 px-3 text-right font-medium tabular-nums ${rateColor}`}>{row.resolveRate}%</td>
                          <td className={`py-2.5 px-3 text-right tabular-nums ${effortColor}`}>{row.avgEffort}</td>
                          <td className="py-2.5 pl-3 w-[180px] min-w-[140px]">
                            <CategoryBar 
                              height={6}
                              segments={[
                                { label: t('widgets.config.statistics.overview.resolution.resolved'), value: row.resolved, color: COLORS.emerald },
                                { label: t('widgets.config.statistics.overview.resolution.partial'), value: row.partial, color: COLORS.orange },
                                { label: t('widgets.config.statistics.overview.resolution.unresolved'), value: row.unresolved, color: COLORS.red },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WidgetStatistics;
